import { ReactNode, useEffect, useRef } from "react";
import { getEnvVar } from "../../../envUtils";
import { v4 as uuidv4 } from "uuid";
import useUrlParams from "../hooks/useUrlParams";
import useSocket from "../hooks/useSocket";

interface SocketProviderProps {
    children: ReactNode;
}

const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const { scenario_id } = useUrlParams();
    const { setSocket } = useSocket();

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef<number>(0);
    const isUnmountedRef = useRef<boolean>(false);

    const create_socket = (sessionId: string): WebSocket | null => {
        try {
            return new WebSocket(
                `${getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL')}test/operate/eventhandler?resourceId=${scenario_id}&sessionId=${sessionId}`
            );
        } catch (error) {
            console.error("Failed to create WebSocket:", error);
            return null;
        }
    };

    useEffect(() => {
        if (!scenario_id) return;

        isUnmountedRef.current = false;
        reconnectAttemptsRef.current = 0;

        const connect = () => {
            if (isUnmountedRef.current) return;

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            const token = sessionStorage.getItem("authentication");
            if (!token) {
                console.warn("WebSocket connect deferred: No authentication token found");
                return;
            }

            const sessionId = uuidv4();
            const ws = create_socket(sessionId);
            if (!ws) {
                scheduleReconnect();
                return;
            }

            wsRef.current = ws;

            ws.onopen = () => {
                if (isUnmountedRef.current) {
                    ws.close();
                    return;
                }
                ws.send(JSON.stringify({ type: 'authorization', token: `${token}` }));
                console.log('Socket Connected');
                reconnectAttemptsRef.current = 0;
                setSocket(ws);

                // Re-sync execution state upon reconnection to catch up on any missed events
                window.dispatchEvent(new Event("validation_status_refetch"));
            };

            ws.onclose = (event) => {
                console.log(`Socket Disconnected (code: ${event?.code || 'unknown'})`);
                if (!isUnmountedRef.current) {
                    scheduleReconnect();
                }
            };

            ws.onerror = (error) => {
                console.error('Socket Error:', error);
            };
        };

        const scheduleReconnect = () => {
            if (isUnmountedRef.current) return;
            if (reconnectTimeoutRef.current) return;

            // Exponential backoff: 1.5s, 3s, 6s, max 10s
            const delay = Math.min(1500 * Math.pow(2, reconnectAttemptsRef.current), 10000);
            reconnectAttemptsRef.current += 1;
            console.log(`Socket will attempt reconnect in ${delay}ms (attempt #${reconnectAttemptsRef.current})`);

            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectTimeoutRef.current = null;
                connect();
            }, delay);
        };

        connect();

        return () => {
            isUnmountedRef.current = true;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            setSocket(null);
        };
    }, [scenario_id]);

    return children as React.ReactElement;
};

export default SocketProvider;